import axios from "axios";
import { defineStore } from "pinia";
import { ref } from "vue";
import { apiClient } from "@/client";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";

export interface ReviewItem {
	id: string;
	asset_type: string;
	acquired: string;
	cloud_cover: number | null;
}

export interface ReviewPool {
	id: number;
	uri: string;
	condition: string;
	geometry: any;
	reviewed: boolean;
	reviewerId: number | null;
	reviewedAt: string | null;
	createdAt: string;
	itemId: string | null;
}

export interface ReviewTileDetail {
	z: number;
	x: number;
	y: number;
	items: ReviewItem[];
	pools: ReviewPool[];
}

export const useVisionReviewStore = defineStore("visionReview", () => {
	const detail = ref<ReviewTileDetail | null>(null);
	const loading = ref(false);
	const error = ref<string | null>(null);
	// Pools created by a reviewer drawing on the current tile. Shown in a
	// dedicated table so a freshly drawn pool can be corrected or removed
	// before it is accepted.
	const createdPools = ref<ReviewPool[]>([]);

	SSEManager.subscribe(async (msg: SSEMessageResource) => {
		if (msg.resource !== "sync:pool") return;
		const poolId = uriID(msg.uri);
		if (!poolId || !detail.value) return;

		switch (msg.type) {
			case "created": {
				const pool = await fetchPool(poolId);
				if (!pool) return;
				if (createdPools.value.some((p) => p.id === poolId)) return;
				if (!poolInTile(pool, detail.value)) return;
				createdPools.value.unshift(pool);
				if (!detail.value.pools.some((p) => p.id === poolId)) {
					detail.value.pools.push(pool);
				}
				break;
			}
			case "updated": {
				const pool = await fetchPool(poolId);
				if (!pool) return;
				const idx = detail.value.pools.findIndex((p) => p.id === poolId);
				if (idx >= 0) {
					detail.value.pools[idx] = pool;
				} else {
					detail.value.pools.push(pool);
				}
				const cIdx = createdPools.value.findIndex((p) => p.id === poolId);
				if (cIdx >= 0) createdPools.value[cIdx] = pool;
				break;
			}
			case "deleted":
				detail.value.pools = detail.value.pools.filter((p) => p.id !== poolId);
				createdPools.value = createdPools.value.filter((p) => p.id !== poolId);
				break;
		}
	});

	async function fetchPool(id: number): Promise<ReviewPool | null> {
		try {
			return await apiClient.JSONGet<ReviewPool>(
				`/api/vision/train/pool/${encodeURIComponent(id)}`,
			);
		} catch (e: any) {
			console.error("Failed to fetch pool:", id, e);
			return null;
		}
	}

	async function loadItemTileDetail(
		itemId: string,
		z: number,
		x: number,
		y: number,
	) {
		loading.value = true;
		error.value = null;
		createdPools.value = [];
		try {
			detail.value = await apiClient.JSONGet<ReviewTileDetail>(
				`/api/vision/review/item/${encodeURIComponent(itemId)}/${z}/${x}/${y}`,
			);
		} catch (e: unknown) {
			error.value = apiErrorMessage(e, "Failed to load tile detail.");
		} finally {
			loading.value = false;
		}
	}

	return {
		detail,
		createdPools,
		loading,
		error,
		loadItemTileDetail,
	};
});

function uriID(uri: string): number | null {
	const parts = uri.split("/");
	const last = parts[parts.length - 1];
	const id = parseInt(last, 10);
	return isNaN(id) ? null : id;
}

function tileToLng(tx: number, tz: number): number {
	return (tx / Math.pow(2, tz)) * 360 - 180;
}

function tileToLat(ty: number, tz: number): number {
	const latRad = Math.atan(
		Math.sinh(Math.PI * (1 - (2 * ty) / Math.pow(2, tz))),
	);
	return (latRad * 180) / Math.PI;
}

// True when the pool's geometry bounding box overlaps the given tile. Used to
// ignore pool `created` events for other tiles (the SSE feed is global).
function poolInTile(pool: ReviewPool, detail: ReviewTileDetail): boolean {
	if (!pool.geometry) return false;
	const west = tileToLng(detail.x, detail.z);
	const east = tileToLng(detail.x + 1, detail.z);
	const north = tileToLat(detail.y, detail.z);
	const south = tileToLat(detail.y + 1, detail.z);

	const coords =
		pool.geometry.type === "Polygon"
			? pool.geometry.coordinates
			: pool.geometry.type === "MultiPolygon"
				? pool.geometry.coordinates.flat()
				: null;
	if (!coords) return false;

	let minX = Infinity;
	let maxX = -Infinity;
	let minY = Infinity;
	let maxY = -Infinity;
	for (const ring of coords) {
		for (const [lng, lat] of ring) {
			if (lng < minX) minX = lng;
			if (lng > maxX) maxX = lng;
			if (lat < minY) minY = lat;
			if (lat > maxY) maxY = lat;
		}
	}
	return maxX >= west && minX <= east && maxY >= south && minY <= north;
}

// Extract a user-presentable message from an axios failure (the server's
// {message} JSON body) or any other thrown error.
function apiErrorMessage(e: unknown, fallback: string): string {
	if (axios.isAxiosError<{ message?: string }>(e)) {
		return e.response?.data?.message || e.message || fallback;
	}
	if (e instanceof Error && e.message) return e.message;
	return fallback;
}
